import { useState } from "react";
import { Download } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { grnApi, procPdfApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, useMutation } from "../../hooks";

export default function GRNViewPage() {
  const { id } = useParams<{ id: string }>();
  const [downloading, setDownloading] = useState(false);

  const { data: grn, loading, error, reload } = useAsyncData(
    () => grnApi.get(Number(id)),
    [id],
  );

  const { mutate: doAccept, loading: accepting, error: acceptErr } = useMutation(() =>
    grnApi.accept(Number(id)).then(() => reload()),
  );

  const { mutate: doReject, loading: rejecting, error: rejectErr } = useMutation(() =>
    grnApi.reject(Number(id)).then(() => reload()),
  );

  async function downloadPdf() {
    if (!grn) return;
    setDownloading(true);
    try {
      const blob = await procPdfApi.grn(Number(id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `grn-${grn.serialNo ?? id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  }

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!grn) return null;

  const actionErr = acceptErr || rejectErr;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/procurement/grns" className="text-sm text-emerald-700 hover:underline">&larr; Back to GRNs</Link>
        <button onClick={downloadPdf} disabled={downloading}
          className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <Download className="h-4 w-4" />
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>

      <ProcForm formNo="PROC-FRM-05" title="Goods Received Note" serialNo={grn.serialNo} date={grn.date}>
        <ProcSection title="GRN Details">
          <ProcGrid>
            <ProcField label="LPO" value={grn.lpoSerialNo ?? String(grn.lpoId)} />
            <ProcField label="Received From" value={grn.receivedFrom} />
            <ProcField label="Delivery Note No" value={grn.deliveryNoteNo} />
            <ProcField label="Status" value={<ProcStatusBadge value={grn.status} />} />
          </ProcGrid>
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Qty Ordered</th>
                  <th className="px-3 py-2 text-right">Qty Received</th>
                  <th className="px-3 py-2 text-right">Variance</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {grn.lines.map((l) => {
                  const variance = l.qtyReceived - l.qtyOrdered;
                  return (
                    <tr key={l.id ?? l.lineOrder}>
                      <td className="px-3 py-2 text-slate-400">{l.lineOrder}</td>
                      <td className="px-3 py-2">{l.description}</td>
                      <td className="px-3 py-2 text-right">{l.qtyOrdered}</td>
                      <td className="px-3 py-2 text-right">{l.qtyReceived}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${variance < 0 ? "text-red-600" : "text-slate-700"}`}>
                        {variance >= 0 ? "+" : ""}{variance}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{l.remarks || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ProcSection>

        {grn.status === "DRAFT" && (
          <ProcSection title="Actions">
            {actionErr && <ProcError message={actionErr} />}
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => doAccept()} disabled={accepting} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
                {accepting ? "Accepting…" : "Accept GRN"}
              </button>
              <button type="button" onClick={() => doReject()} disabled={rejecting} className="rounded-xl border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
                {rejecting ? "Rejecting…" : "Reject GRN"}
              </button>
            </div>
          </ProcSection>
        )}
      </ProcForm>
    </div>
  );
}
